import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import AuditPopover from '../components/AuditPopover';
import SearchableSelect from '../components/SearchableSelect';
import QuickListManager from '../components/QuickListManager';
import DataTable from '../components/DataTable';
import { Button, Modal, FormField, ListPageHeader, FilterBar } from '../components/ui';
import { CardHero, CardSplitButton } from '../components/card';
import { TAB_ICONS } from '../utils/tabIcons';

const personnelLabel = (p) => p.full_name || ((p.first_name || '') + ' ' + (p.last_name || '')).trim();
const personnelSubLabel = (p) => {
  const parts = [];
  if (p.code) parts.push(p.code);
  if (p.department) parts.push(p.department);
  if (p.position) parts.push(p.position);
  return parts.filter(Boolean).join(' | ');
};
const personnelSearchKeys = ['full_name', 'first_name', 'last_name', 'code', 'department', 'position', 'mobile'];

const TX_CONFIG = {
  credit: { label: 'Personel Alacak Dekontları', color: '#16a34a', sign:  1 },
  debit:  { label: 'Personel Borç Dekontları',   color: '#dc2626', sign: -1 },
};

const emptyTx = {
  transaction_date: new Date().toISOString().substring(0, 10),
  description: '', amount: '',
  personnel_id: '', special_code1: '', special_code2: '',
};

function fmt(n) {
  return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
}
function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('tr-TR');
}

export default function PersonnelVoucherPage({ txType, autoOpenId = null }) {
  const cfg = TX_CONFIG[txType] || TX_CONFIG.credit;

  const [transactions, setTransactions] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [filters, setFilters] = useState({ date_from: '', date_to: '', personnel_id: '' });

  const [modalMode, setModalMode] = useState(null); // 'view' | 'edit' | 'add'
  const [selectedTx, setSelectedTx] = useState(null);
  const [txForm, setTxForm] = useState({ ...emptyTx });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [specialCodes1, setSpecialCodes1] = useState([]);
  const [specialCodes2, setSpecialCodes2] = useState([]);
  const [showQuickList, setShowQuickList] = useState(null);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = { type: txType };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get('/personnel/all-transactions', { params });
      if (res.data.success) setTransactions(res.data.data || []);
    } catch (err) {
      console.error('personnel all-transactions error:', err.response?.data || err.message);
    } finally { setLoading(false); }
  }, [txType, filters]);

  const fetchPersonnel = useCallback(async () => {
    try {
      const res = await api.get('/personnel', { params: { limit: 500 } });
      if (res.data.success) setPersonnel((res.data.data.items || []).filter(p => p.is_active));
    } catch {}
  }, []);

  const fetchSpecialCodes = useCallback(async () => {
    try {
      const r = await api.get('/invoices/lookups/all');
      if (r.data.success) {
        setSpecialCodes1(r.data.data?.specialCodes1 || []);
        setSpecialCodes2(r.data.data?.specialCodes2 || []);
      }
    } catch {}
  }, []);

  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

  // Deep-link: autoOpenId varsa o hareketi aç
  useEffect(() => {
    if (!autoOpenId) return;
    const tx = (transactions || []).find(t => t.id === autoOpenId);
    if (tx) openView(tx);
  }, [autoOpenId, transactions]);
  useEffect(() => { fetchPersonnel(); }, [fetchPersonnel]);
  useEffect(() => { fetchSpecialCodes(); }, [fetchSpecialCodes]);

  const closeModal = () => { setModalMode(null); setSelectedTx(null); setMsg(null); };

  const buildFormFromTx = (tx) => {
    const dateStr = tx.transaction_date
      ? new Date(tx.transaction_date).toISOString().substring(0, 10)
      : emptyTx.transaction_date;
    return {
      ...emptyTx,
      transaction_date: dateStr,
      description: tx.description || '',
      amount: String(tx.amount || ''),
      personnel_id: tx.personnel_id || '',
      special_code1: tx.special_code1 || '',
      special_code2: tx.special_code2 || '',
    };
  };

  const openView = (tx) => {
    setTxForm(buildFormFromTx(tx));
    setSelectedTx(tx);
    setMsg(null);
    setModalMode('view');
  };

  const openAdd = () => {
    setTxForm({ ...emptyTx });
    setSelectedTx(null);
    setMsg(null);
    setModalMode('add');
  };

  const handleSave = async (mode = 'close') => {
    if (mode && typeof mode === 'object') mode = 'close';
    const { personnel_id, transaction_date, description, amount } = txForm;
    if (modalMode === 'add' && !personnel_id) return setMsg({ type: 'error', text: 'Personel seçin.' });
    if (!transaction_date || !description || !amount) return setMsg({ type: 'error', text: 'Tarih, açıklama ve tutar zorunludur.' });
    if (!(parseFloat(amount) > 0)) return setMsg({ type: 'error', text: 'Tutar 0\'dan büyük olmalı.' });

    setSaving(true); setMsg(null);
    try {
      const body = {
        transaction_date,
        description,
        amount: parseFloat(amount),
        special_code1: txForm.special_code1 || null,
        special_code2: txForm.special_code2 || null,
      };
      if (modalMode === 'edit') {
        await api.put('/personnel/' + selectedTx.personnel_id + '/transactions/' + selectedTx.id, body);
      } else {
        body.transaction_type = txType;
        await api.post('/personnel/' + personnel_id + '/transactions', body);
      }
      await fetchTransactions();
      if (mode === 'close') closeModal();
      else if (mode === 'new') { setMsg({ type: 'success', text: 'Kaydedildi — yeni dekont' }); setTimeout(() => setMsg(null), 2000); }
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Kaydedilemedi.' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!selectedTx) return;
    if (!window.confirm('Bu dekontu silmek istiyor musunuz?')) return;
    try {
      await api.delete('/personnel/' + selectedTx.personnel_id + '/transactions/' + selectedTx.id);
      closeModal();
      fetchTransactions();
    } catch (err) {
      alert(err.response?.data?.message || 'Silinemedi.');
    }
  };

  const tf = (key, val) => setTxForm(prev => ({ ...prev, [key]: val }));

  const filtered = transactions.filter(tx =>
    !search ||
    (tx.document_no || '').toLowerCase().includes(search.toLowerCase()) ||
    (tx.description || '').toLowerCase().includes(search.toLowerCase()) ||
    (tx.personnel_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (tx.personnel_code || '').toLowerCase().includes(search.toLowerCase())
  );

  const ro = modalMode === 'view';
  const selectedPersonnel = personnel.find(p => p.id === txForm.personnel_id);

  // Modal aksiyonları — footer'a taşındı (yeni Modal API)
  const inEditAdd = modalMode === 'edit' || modalMode === 'add';
  const inView = modalMode === 'view';
  const isAdd = modalMode === 'add';

  return (
    <div className="stocks-page">
      <ListPageHeader
        title={cfg.label}
        count={filtered.length}
        unit="kayit"
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Ara..."
        onRefresh={fetchTransactions}
        refreshing={loading}
        actions={<Button size="sm" onClick={openAdd}>+ Yeni Dekont</Button>}
      />

      <FilterBar
        show={showFilterBar}
        onToggle={() => setShowFilterBar(s => !s)}
        values={filters}
        onChange={setFilters}
        fields={[
          { key: 'date_from', type: 'date', label: 'İlk Tarih' },
          { key: 'date_to',   type: 'date', label: 'Son Tarih' },
          { key: 'personnel_id', type: 'select', label: 'Personel', flex: '1 1 240px', minWidth: 220,
            options: [{ value: '', label: 'Tüm Personel' }, ...personnel.map(p => ({ value: p.id, label: personnelLabel(p) + (p.code ? ` (${p.code})` : '') }))] },
        ]}
      />

      <div className="stocks-table-wrap">
        <DataTable
          pageKey={`personnel-voucher-${txType}-list`}
          tableClassName="stocks-table"
          rowClassName="stocks-table-row"
          loading={loading}
          emptyText="Kayıt bulunamadı"
          data={filtered}
          rowKey="id"
          onRowClick={tx => openView(tx)}
          columns={[
            { id: 'doc_no', header: 'Belge No', accessor: 'document_no', sortable: true, width: 130,
              cell: tx => <span style={{ fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 600, color: 'var(--accent-primary)', whiteSpace: 'nowrap' }}>{tx.document_no || '—'}</span> },
            { id: 'date', header: 'Tarih', accessor: 'transaction_date', sortable: true, width: 110,
              sortFn: (a,b) => new Date(a.transaction_date||0)-new Date(b.transaction_date||0),
              cell: tx => <span style={{ whiteSpace: 'nowrap' }}>{fmtDate(tx.transaction_date)}</span> },
            { id: 'personnel', header: 'Personel', accessor: tx => (tx.personnel_name||'')+' '+(tx.personnel_code||''), sortable: true,
              cell: tx => <><div style={{ fontWeight: 600 }}>{tx.personnel_name}</div><div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>{tx.personnel_code}</div></> },
            { id: 'description', header: 'Açıklama', accessor: 'description', sortable: true,
              cell: tx => <div>{tx.description}</div> },
            { id: 'amount', header: 'Tutar', accessor: tx => parseFloat(tx.amount)||0, sortable: true, width: 140,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' },
              cell: tx => <span style={{ color: cfg.color }}>{cfg.sign > 0 ? '+' : '-'}{fmt(tx.amount)} TRY</span> },
            { id: 'audit', header: '', sortable: false, hideable: false, draggable: false, filterable: false, width: 32,
              cellStyle: { textAlign: 'center' },
              cell: tx => <span onClick={e => e.stopPropagation()}><AuditPopover endpoint={`/personnel/transactions/${tx.id}/audit`} /></span> },
          ]}
        />
      </div>

      {modalMode !== null && (
        <Modal
          icon={null}
          title={`${modalMode === 'add' ? 'Yeni: ' : ''}${cfg.label.replace('Personel ', '')}`}
          size="lg"
          onClose={closeModal}
          footer={
            <>
              <div className="ui-modal-footer-left">
                {inView && <Button variant="danger" onClick={handleDelete}>🗑 Sil</Button>}
              </div>
              <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {inEditAdd && <Button variant="ghost" onClick={() => { if (isAdd) closeModal(); else { setMsg(null); setModalMode('view'); } }}>{isAdd ? 'Vazgeç' : 'İptal'}</Button>}
                {inEditAdd && (
                  <CardSplitButton
                    primaryLabel="💾 Kaydet"
                    primaryAction={() => handleSave('close')}
                    loading={saving}
                    options={[
                      { label: 'Kaydet ve Yeni Aç', icon: '✚', onClick: () => handleSave('new') },
                      { label: 'Kaydet ve Kapat',  icon: '✓', onClick: () => handleSave('close') },
                    ]}
                  />
                )}
                {inView && <Button onClick={() => { setMsg(null); setModalMode('edit'); }}>✎ Düzenle</Button>}
              </div>
            </>
          }
        >
          {modalMode !== 'add' && selectedTx && (
            <CardHero
              icon="👤"
              code={selectedTx.document_no}
              title={selectedTx.personnel_name || 'Personel Dekontu'}
              subtitle={selectedTx.date ? new Date(selectedTx.date).toLocaleDateString('tr-TR') : null}
              kpis={[
                selectedTx.amount != null && {
                  label: 'Tutar', value: fmt(selectedTx.amount), color: 'primary', sublabel: 'TRY',
                },
              ].filter(Boolean)}
            />
          )}
          {msg && (
            <div style={{
              marginBottom: 12, padding: '9px 12px', borderRadius: 8, fontSize: '0.84rem',
              background: msg.type === 'error' ? 'var(--danger-bg)' : 'rgba(16,185,129,0.1)',
              color: msg.type === 'error' ? 'var(--danger)' : '#0d9488',
              border: '1px solid ' + (msg.type === 'error' ? 'rgba(239,68,68,0.25)' : 'rgba(16,185,129,0.25)'),
            }}>{msg.text}</div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            <FormField label="Belge No">
              <input
                disabled
                value={modalMode === 'add' ? 'Otomatik' : (selectedTx?.document_no || '—')}
                style={{ fontFamily: 'monospace', opacity: 0.7 }}
              />
            </FormField>
            <div />

            <FormField label="Personel" required={!ro && modalMode === 'add'}>
              {(ro || modalMode === 'edit') ? (
                <input disabled value={selectedTx ? `${selectedTx.personnel_code || ''} — ${selectedTx.personnel_name || ''}` : (selectedPersonnel ? personnelLabel(selectedPersonnel) : '—')} />
              ) : (
                <SearchableSelect
                  value={txForm.personnel_id}
                  onChange={id => tf('personnel_id', id)}
                  options={personnel}
                  getLabel={personnelLabel}
                  getSubLabel={personnelSubLabel}
                  searchKeys={personnelSearchKeys}
                  placeholder="Personel seç — ad, kod, departman ile ara"
                />
              )}
            </FormField>

            <FormField label="Tarih" required={!ro}>
              <input type={ro ? 'text' : 'date'}
                disabled={ro}
                value={ro ? fmtDate(txForm.transaction_date) : txForm.transaction_date}
                onChange={e => tf('transaction_date', e.target.value)}
              />
            </FormField>

            <FormField label="Tutar (TRY)" required={!ro}>
              <input type={ro ? 'text' : 'number'} min="0" step="0.01"
                disabled={ro}
                placeholder={ro ? '' : '0.00'}
                value={ro ? fmt(txForm.amount) : txForm.amount}
                onChange={e => tf('amount', e.target.value)}
              />
            </FormField>

            <FormField label="Açıklama" required={!ro} fullWidth>
              <input disabled={ro} value={txForm.description} onChange={e => tf('description', e.target.value)} placeholder={ro ? '' : 'İşlem açıklaması'} />
            </FormField>

            <FormField label={<>Özel Kod 1 {!ro && <button type="button" className="quick-add-btn" onClick={() => setShowQuickList('special1')}>+</button>}</>}>
              <select disabled={ro} value={txForm.special_code1 || ''} onChange={e => tf('special_code1', e.target.value)}>
                <option value="">Seçiniz</option>
                {specialCodes1.map(s => <option key={s.id} value={s.code || s.name}>{s.code ? s.code + ' - ' : ''}{s.name}</option>)}
              </select>
            </FormField>
            <FormField label={<>Özel Kod 2 {!ro && <button type="button" className="quick-add-btn" onClick={() => setShowQuickList('special2')}>+</button>}</>}>
              <select disabled={ro} value={txForm.special_code2 || ''} onChange={e => tf('special_code2', e.target.value)}>
                <option value="">Seçiniz</option>
                {specialCodes2.map(s => <option key={s.id} value={s.code || s.name}>{s.code ? s.code + ' - ' : ''}{s.name}</option>)}
              </select>
            </FormField>
          </div>
        </Modal>
      )}

      {showQuickList && (
        <QuickListManager
          open={true}
          title={showQuickList === 'special1' ? 'Özel Kod 1' : 'Özel Kod 2'}
          items={showQuickList === 'special1' ? specialCodes1 : specialCodes2}
          apiBase="/invoices/special-codes"
          extraBody={{ code_type: showQuickList }}
          onClose={() => setShowQuickList(null)}
          onChanged={fetchSpecialCodes}
        />
      )}
    </div>
  );
}
